// Strips version/credit noise from a track TITLE for DISPLAY.
//
// Started as a straight port of src/services/lyricsService.js's
// cleanTitle() composed with src/services/trackFeaturesService.js's
// stripVersionNoise() - the same formula this codebase already runs on
// every title before querying LRCLIB/Deezer/TheAudioDB, because Sonos's
// raw metadata carries exactly this noise ("(feat. X)", "- Radio Edit",
// "(Extended Mix)") and it throws off catalog matching there for the same
// reason it looks bad on screen here.
//
// The dash rule has since been widened past that original: the server-side
// version only strips a trailing "- <suffix>" when <suffix> matches a known
// version-noise keyword (mix/edit/remaster/live/...), to avoid mangling a
// title where the dash is doing something else. Requested explicitly here
// instead: EVERYTHING from the first real title-separator dash onward is
// dropped, keyword or not - simpler, and covers whatever suffix shows up
// next without needing its own keyword added first.
//
// "Real title-separator dash" means one with actual whitespace on both
// sides (" - ", however it is written by an artist/label) - a bare hyphen
// with no surrounding space is treated as part of a word ("Half-Life",
// "Un-Break My Heart") and left alone. This is the one piece of judgment
// call in an otherwise blanket rule, and it is load-bearing: without it,
// "Half-Life" would be cut down to "Half".
//
// Bracket-noise removal is still the original codebase's own formula
// (server-side is CommonJS/Node-only, so this file remains the browser
// port of it) - mirror any change made there.

function normalizePunctuation(value) {
  return (value || '')
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[–—]/g, '-');
}

// EVERYTHING in brackets goes, keyword or not. This used to be a keyword
// list ("(feat. X)", "(Remastered)", ...) that let an unrecognized bracket
// through; the stage displays (SplitFlap, TypeSlam) asked for the blanket
// rule instead - same reasoning as the dash rule below, one line down.
function stripBracketNoise(title) {
  return title.replace(/\s*[([][^)\]]*[)\]]/g, '');
}

// Everything from the first comma onward. "Queen, David Bowie" -> "Queen",
// "Love Me, Love Me" -> "Love Me" - by explicit request the comma is a hard
// cut for the stage displays, accepting the second case as the price of the
// first.
function stripAfterComma(title) {
  const i = title.indexOf(',');
  return i < 0 ? title : title.slice(0, i);
}

// Everything from the first REAL separator dash onward - see the file
// comment for why "real" requires whitespace on both sides.
function stripAfterDash(title) {
  const m = title.match(/\s+-\s+/);
  if (!m) return title;
  return title.slice(0, m.index);
}

// Falls back to the trimmed original if cleaning would leave nothing (a
// title that is ENTIRELY noise, e.g. just "(Remix)", should still show
// something rather than go blank).
export function cleanDisplayTitle(title) {
  const raw = (title || '').trim();
  if (!raw) return raw;

  let t = normalizePunctuation(raw).trim();
  t = stripBracketNoise(t);
  t = stripAfterDash(t);
  t = stripAfterComma(t);
  t = t.replace(/\s*[([]\d{4}[)\]]\s*$/g, ''); // trailing "(1999)" left bare by the dash cut
  t = t.replace(/\s{2,}/g, ' ').trim();

  return t || raw;
}

// The ARTIST, same blanket rules: "Daft Punk (feat. Pharrell)" and
// "Queen, David Bowie" both come back as the lead name alone. Kept as its
// own export rather than aliasing cleanDisplayTitle so the two can diverge
// again later without touching call sites.
export function cleanDisplayArtist(artist) {
  return cleanDisplayTitle(artist);
}
